import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Events, track } from "apis/procore";
import { CORE_PROPERTIES_TAB_IDS, usePropertiesTabs } from "contexts/propertiesTabs";
import { BaseWindowProps } from "contexts/windows/reducer";
import { useObjectData } from "hooks/useObjectData";

import { Window } from "../../window";
import { PropertiesWindowHeader } from "./components";

/**
 * Properties Window Component
 *
 * Displays object properties with search and tabbed interface.
 * Uses the new window infrastructure system.
 */
export function PropertiesWindow({ onClose, position = "center", ...props }: BaseWindowProps) {
    const { object, hasAccess, isLoading } = useObjectData();
    const customTabs = usePropertiesTabs();
    const [searchTerm, setSearchTerm] = useState("");

    const [activeTab, setActiveTab] = useState<string>(() => {
        return Object.keys(customTabs.tabEntries)[0] || CORE_PROPERTIES_TAB_IDS.PROPERTIES;
    });

    const [searchEmptyState, setSearchEmptyState] = useState(false);
    const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
            searchDebounceRef.current = null;
        }
        const trimmed = searchTerm.trim();
        if (!trimmed) {
            return;
        }
        searchDebounceRef.current = setTimeout(() => {
            void track(Events.propertyPanel.searched, { search_term: trimmed });
        }, 500);
        return () => {
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
                searchDebounceRef.current = null;
            }
        };
    }, [searchTerm]);
    const isSearchActive = searchTerm.trim().length > 0;
    const { t } = useTranslation();

    const error = object && !hasAccess ? t("objectAccessForbidden") : null;

    const objectName = object?.base.find(([key]) => key === "Name")?.[1] || (object ? `Object ${object.id}` : "");

    const getEmptyState = () => {
        if (!object) {
            return {
                title: t("selectAnObject"),
                message: t("selectObjectForProperties"),
            };
        }

        if (activeTab === CORE_PROPERTIES_TAB_IDS.PROPERTIES && isSearchActive && searchEmptyState) {
            return {
                title: t("noPropertiesMatchSearch"),
                message: t("pleaseRetrySearch"),
            };
        }

        return undefined;
    };

    const renderActiveTab = () => {
        const tabEntry = customTabs.tabEntries[activeTab];
        if (!tabEntry) {
            return <div data-testid="properties-tab-not-found">Tab not found</div>;
        }

        const TabComponent = tabEntry.config.component;

        return (
            <div data-testid={`properties-tab-content-${activeTab}`}>
                <TabComponent
                    object={object}
                    hasAccess={hasAccess}
                    searchTerm={searchTerm}
                    isSearchActive={isSearchActive}
                    onEmptyStateChange={setSearchEmptyState}
                />
            </div>
        );
    };

    return (
        <Window
            position={position}
            size={{ width: 400, height: 500 }}
            loading={isLoading}
            loadingMessage={t("loadingProperties")}
            error={error}
            emptyState={getEmptyState()}
            header={
                <PropertiesWindowHeader
                    objectName={objectName}
                    activeTab={activeTab}
                    searchTerm={searchTerm}
                    onTabChange={setActiveTab}
                    onSearchChange={setSearchTerm}
                    onClose={onClose}
                />
            }
            onClose={onClose}
            secondaryBgColor={true}
            data-testid="properties-window"
            {...props}
        >
            {renderActiveTab()}
        </Window>
    );
}
